/**
 * A sidebar menu's icons sit in equal slots, the same gap apart, in its
 * header (hidden, favorites, folders, sort, pin) and on its rows (⋮, eye,
 * heart, drag handle), and
 * the last of each ends the same distance from the card's right edge, so a
 * row's icons line up under the header's. A row ends MENU_ROW_END_PAD_CLASS
 * inside the list's own inset; the header's right padding is both.
 */
export const MENU_ICON_SLOT_CLASS = 'flex h-6 w-6 shrink-0 items-center justify-center rounded'

/** A row of icon slots, in a header or at a row's end. */
export const MENU_ICON_SLOTS_CLASS = 'flex shrink-0 items-center gap-0.5'

/**
 * A row's icon slot. At rest a row's name runs to its first icon that shows
 * (a favorite's heart, a hidden page's eye) and over the room of the ones
 * that show only on hover; hovered, those come in and the name shortens,
 * fading at its new end. So a slot is `shown`, `held` (empty at rest but
 * keeping its room, right of a shown one, so the shown one never moves), or
 * `collapsed` (no room at rest, left of every shown one).
 */
export function menuIconSlotClass(state: 'shown' | 'held' | 'collapsed') {
  if (state === 'shown') return 'flex'
  if (state === 'held') return 'flex invisible group-hover:visible'
  return 'hidden group-hover:flex'
}

/**
 * The icon slots' state left to right, from which of them show at rest: a
 * slot right of a shown one is held, one left of them all collapses.
 */
export function menuIconSlotStates(
  shownAtRest: readonly boolean[]
): ('shown' | 'held' | 'collapsed')[] {
  const firstShown = shownAtRest.indexOf(true)
  return shownAtRest.map((shown, index) =>
    shown ? 'shown' : firstShown !== -1 && index > firstShown ? 'held' : 'collapsed'
  )
}

/**
 * The gap before a row's icon slots: always while one shows at rest,
 * otherwise only while they come in on hover, so a name with no icon beside
 * it runs to the row's end.
 */
export function menuIconSlotsLeadClass(anyShownAtRest: boolean) {
  return anyShownAtRest ? 'pl-2' : 'pl-0 group-hover:pl-2'
}

/** A row's right padding, after its last icon. */
export const MENU_ROW_END_PAD_CLASS = 'pr-1'

/** A menu header's right padding: the list's inset and a row's own. */
export const MENU_HEADER_END_PAD_CLASS = 'pr-[calc(var(--sidebar-item-inset)+0.25rem)]'
